import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, 
  Users, 
  Building2, 
  FileText, 
  TrendingUp,
  LogOut,
  Crown,
  RefreshCw,
  Trash2
} from 'lucide-react';
import axios from 'axios';
import { formatCurrency, formatDate } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const adminApi = axios.create({ baseURL: API_URL });

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [changePlanDialog, setChangePlanDialog] = useState({ open: false, empresa: null });
  const [selectedPlan, setSelectedPlan] = useState('');

  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  useEffect(() => {
    if (!localStorage.getItem('adminToken')) {
      navigate('/admin');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const [statsRes, empresasRes, usuariosRes] = await Promise.all([
        adminApi.get('/admin/stats'),
        adminApi.get('/admin/empresas'),
        adminApi.get('/admin/usuarios')
      ]);
      setStats(statsRes.data);
      setEmpresas(empresasRes.data);
      setUsuarios(usuariosRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin');
      } else {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin');
  };

  const handleChangePlan = async () => {
    if (!changePlanDialog.empresa || !selectedPlan) return;
    
    try {
      await adminApi.put(`/admin/empresas/${changePlanDialog.empresa.id}/plan?plan=${selectedPlan}`);
      toast.success(`Plan actualizado a ${selectedPlan}`);
      setChangePlanDialog({ open: false, empresa: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar plan');
    }
  };

  const handleDeleteEmpresa = async (empresa) => {
    if (!window.confirm(`¿Eliminar empresa "${empresa.nombre_comercial}" y todos sus datos?`)) return;
    
    try {
      await adminApi.delete(`/admin/empresas/${empresa.id}`);
      toast.success('Empresa eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar empresa');
    }
  };

  const openChangePlanDialog = (empresa) => {
    setSelectedPlan(empresa.plan);
    setChangePlanDialog({ open: true, empresa });
  };

  const getPlanBadge = (plan) => {
    const colors = {
      FREE: 'bg-slate-100 text-slate-700',
      PRO: 'bg-blue-100 text-blue-700',
      BUSINESS: 'bg-violet-100 text-violet-700'
    };
    return <Badge className={colors[plan] || colors.FREE}>{plan}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="spinner border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Panel de Administración
              </h1>
              <p className="text-sm text-slate-400">{adminUser.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={loadData} className="text-slate-400 hover:text-white">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="text-slate-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {['overview', 'empresas', 'usuarios'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 font-medium text-sm transition-colors ${
                  activeTab === tab 
                    ? 'text-amber-500 border-b-2 border-amber-500' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {tab === 'overview' && 'Resumen'}
                {tab === 'empresas' && 'Empresas'}
                {tab === 'usuarios' && 'Usuarios'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6 animate-fadeIn">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">Total Usuarios</p>
                      <p className="text-3xl font-bold text-white mt-1">{stats.total_usuarios}</p>
                    </div>
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">Total Empresas</p>
                      <p className="text-3xl font-bold text-white mt-1">{stats.total_empresas}</p>
                    </div>
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-emerald-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">Total Presupuestos</p>
                      <p className="text-3xl font-bold text-white mt-1">{stats.total_presupuestos}</p>
                    </div>
                    <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-violet-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">Valor Total Cotizado</p>
                      <p className="text-3xl font-bold text-white mt-1">{formatCurrency(stats.valor_total_cotizado)}</p>
                    </div>
                    <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-amber-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Distribution Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Empresas por Plan</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats.empresas_por_plan).map(([plan, count]) => (
                      <div key={plan} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getPlanBadge(plan)}
                        </div>
                        <span className="text-white font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Presupuestos por Estado</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(stats.presupuestos_por_estado).map(([estado, count]) => (
                      <div key={estado} className="flex items-center justify-between">
                        <span className="text-slate-400 capitalize">{estado}</span>
                        <span className="text-white font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Empresas Tab */}
        {activeTab === 'empresas' && (
          <div className="space-y-6 animate-fadeIn">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Todas las Empresas</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Empresa</TableHead>
                      <TableHead className="text-slate-400">Owner</TableHead>
                      <TableHead className="text-slate-400">Plan</TableHead>
                      <TableHead className="text-slate-400">Usuarios</TableHead>
                      <TableHead className="text-slate-400">Presupuestos</TableHead>
                      <TableHead className="text-slate-400">Creada</TableHead>
                      <TableHead className="text-slate-400 w-[120px]">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {empresas.map((empresa) => (
                      <TableRow key={empresa.id} className="border-slate-700">
                        <TableCell className="text-white font-medium">
                          {empresa.nombre_comercial}
                          {empresa.cif_nif && (
                            <span className="block text-sm text-slate-400">{empresa.cif_nif}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-300">
                          {empresa.owner?.nombre || '-'}
                          <span className="block text-sm text-slate-500">{empresa.owner?.email}</span>
                        </TableCell>
                        <TableCell>{getPlanBadge(empresa.plan)}</TableCell>
                        <TableCell className="text-slate-300">{empresa.usuarios_count}</TableCell>
                        <TableCell className="text-slate-300">{empresa.presupuestos_count}</TableCell>
                        <TableCell className="text-slate-400">{formatDate(empresa.created_at)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openChangePlanDialog(empresa)}
                              className="text-amber-500 hover:text-amber-400 hover:bg-slate-700"
                            >
                              <Crown className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteEmpresa(empresa)}
                              className="text-red-500 hover:text-red-400 hover:bg-slate-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Usuarios Tab */}
        {activeTab === 'usuarios' && (
          <div className="space-y-6 animate-fadeIn">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Todos los Usuarios</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Nombre</TableHead>
                      <TableHead className="text-slate-400">Email</TableHead>
                      <TableHead className="text-slate-400">Empresas</TableHead>
                      <TableHead className="text-slate-400">Tipo</TableHead>
                      <TableHead className="text-slate-400">Registro</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usuarios.map((usuario) => (
                      <TableRow key={usuario.id} className="border-slate-700">
                        <TableCell className="text-white font-medium">{usuario.nombre}</TableCell>
                        <TableCell className="text-slate-300">{usuario.email}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {usuario.empresas?.map((e, i) => (
                              <Badge key={i} variant="outline" className="text-slate-300 border-slate-600">
                                {e.nombre_comercial} ({e.rol})
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {usuario.is_admin ? (
                            <Badge className="bg-amber-500/20 text-amber-400">Admin</Badge>
                          ) : (
                            <Badge className="bg-slate-600 text-slate-300">Usuario</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-400">{formatDate(usuario.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Change Plan Dialog */}
      <Dialog open={changePlanDialog.open} onOpenChange={(open) => setChangePlanDialog({ ...changePlanDialog, open })}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Cambiar Plan</DialogTitle>
            <DialogDescription className="text-slate-400">
              Cambiar plan de {changePlanDialog.empresa?.nombre_comercial}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedPlan} onValueChange={setSelectedPlan}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Seleccionar plan" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="FREE" className="text-white">FREE - Starter</SelectItem>
                <SelectItem value="PRO" className="text-white">PRO - Profesional (19€/mes)</SelectItem>
                <SelectItem value="BUSINESS" className="text-white">BUSINESS - Equipos (39€/mes)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setChangePlanDialog({ open: false, empresa: null })} className="text-slate-400">
              Cancelar
            </Button>
            <Button onClick={handleChangePlan} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
